// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useState, useEffect, useRef, useCallback } from 'react';
import { AudioNoiseSuppressionProcessor } from '../utils/audioNoiseSuppressionProcessor';

interface UseAudioNoiseSuppressionProcessorReturn {
  /** Whether the processor is initialized and ready to use */
  isReady: boolean;
  /** Error message if initialization or processing failed */
  error: string | null;
  /** Apply noise filtering to a MediaStream */
  applyNoiseFilter: (stream: MediaStream) => Promise<MediaStream>;
  /** Clean up processor resources */
  cleanup: () => void;
}

/**
 * React hook for managing audio noise suppression with RNNoise
 *
 * Provides automatic initialization, cleanup, and error handling.
 * Falls back to raw audio if noise suppression fails.
 */
export function useAudioNoiseSuppressionProcessor(): UseAudioNoiseSuppressionProcessorReturn {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const processorRef = useRef<AudioNoiseSuppressionProcessor | null>(null);
  const initializingRef = useRef(false);

  // Initialize processor on mount
  useEffect(() => {
    const initProcessor = async (): Promise<void> => {
      if (initializingRef.current) {
        return;
      }

      initializingRef.current = true;

      try {
        console.log('Initializing audio noise suppression processor...');
        const processor = new AudioNoiseSuppressionProcessor();
        await processor.initialize();
        processorRef.current = processor;
        setIsReady(true);
        setError(null);
        console.log('✅ Processor hook initialized successfully');
      } catch (err: any) {
        console.error('Failed to initialize processor:', err);
        const errorMessage =
          err.message || 'Failed to initialize noise suppression. Interview will proceed with standard audio.';
        setError(errorMessage);
        setIsReady(false);
      } finally {
        initializingRef.current = false;
      }
    };

    initProcessor();

    // Cleanup on unmount
    return () => {
      if (processorRef.current) {
        console.log('Cleaning up audio processor on unmount');
        processorRef.current.cleanup();
        processorRef.current = null;
      }
      setIsReady(false);
    };
  }, []);

  /**
   * Apply noise filtering to an audio stream
   */
  const applyNoiseFilter = useCallback(
    async (stream: MediaStream): Promise<MediaStream> => {
      if (!processorRef.current || !isReady) {
        console.warn('Processor not available, returning original stream');
        return stream;
      }

      try {
        console.log('Applying noise filter to audio stream...');
        const processedStream = await processorRef.current.processStream(stream);
        console.log('✅ Noise filter applied successfully');
        return processedStream;
      } catch (err: any) {
        console.error('Failed to apply noise filter:', err);
        setError(err.message || 'Failed to apply noise suppression');
        return stream;
      }
    },
    [isReady]
  );

  /**
   * Manual cleanup function (also happens automatically on unmount)
   */
  const cleanup = useCallback(() => {
    if (processorRef.current) {
      console.log('Manual cleanup requested');
      processorRef.current.cleanup();
      processorRef.current = null;
      setIsReady(false);
    }
  }, []);

  return {
    isReady,
    error,
    applyNoiseFilter,
    cleanup
  };
}
